<template>
  <div class="main-settings-tab" >
    <div>
      <v-sheet outlined rounded class="d-flex align-center pa-3 mt-2">
        <v-icon class="mr-4" color="primary">mdi-server-network</v-icon>

        <div class="d-flex align-start">
          <div class="mr-6">
            <div class="caption text-uppercase grey--text">Main Domain</div>
            <div v-if="!loading && mainDomainData" class="body-2 font-weight-medium">{{ mainDomainData.name}}</div>
          </div>
          <div>
            <div class="caption text-uppercase grey--text">NS Status</div>
            <v-chip
              v-if="!loading && mainDomainData"
              small
              outlined
              :color="mainDomainData && mainDomainData.status === 'active' ? 'success' : 'grey'"
              class="pa-2 text-capitalize"
            >
              {{ mainDomainData.status}}
            </v-chip>
          </div>
        </div>

        <v-spacer />

        <v-btn
          v-if="canReadFeatureConfig"
          outlined
          small
          :color="hasProject ? 'primary' : 'grey lighten-1'"
          class="px-2"
          :disabled="!hasProject"
          @click="goToFeatureConfig"
        >Feature Config
        </v-btn>
      </v-sheet>

      <div v-if="!loading">
        <v-card class="mt-2" outlined>
            <div class="main-settings-tab__geo-header px-4">
              <span class="body-1 black--text">Project geo rules</span>

              <v-tooltip right>
                <template #activator="{ on }">
                  <v-icon small class="ml-1" v-on="on">mdi-information</v-icon>
                </template>
                <span>By default, the domain is available to all countries except those that are blocked</span>
              </v-tooltip>
            </div>

            <v-divider />

          <div class="pa-4">
            <v-card>
              <v-expansion-panels v-model="panel" flat>
                <v-expansion-panel>
                  <v-expansion-panel-header class="main-settings-tab__expansion-header px-4 py-0">
                    <div class="d-flex align-center">
                      <div v-if="blockedGeoDisplay" class="body-2">
                        <strong>Project level block:</strong> {{ blockedGeoDisplay }}
                      </div>
                      <template v-if="canEdit">
                        <v-btn text :disabled="settingsLoading" @click.stop="cancelRules" class="ml-auto">
                          Cancel
                        </v-btn>
                        <v-btn text color="primary" :loading="settingsLoading" @click.stop="saveRules" class="ml-2">
                          <v-icon left small>mdi-content-save-outline</v-icon>
                          Save
                        </v-btn>
                      </template>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="main-settings-tab__expansion-content">
                    <v-divider class="mb-4" />

                    <geo-rules-editor
                      :rules="rules"
                      :readonly="!canEdit"
                      @add="addRule"
                      @remove="removeRule"
                      @update:geo="updateRuleField($event.index, 'geo', $event.value)"
                      @update:devices="updateRuleField($event.index, 'devices', $event.value)"
                      class="ma-4"
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card>
          </div>
        </v-card>

        <v-card class="mt-2" outlined>
          <v-card-text>
            <div class="body-1 black--text mb-4">Password Settings</div>

            <div class="d-flex align-center" style="gap: 24px;">
              <v-text-field
                :value="passwordSettings.login"
                label="Login"
                dense
                hide-details
                readonly
                style="max-width: 200px"
              />

              <div class="d-flex align-center" style="max-width: 220px; flex: 1;">
                <v-text-field
                  :value="passwordSettings.password"
                  label="Password"
                  :type="showPassword ? 'text' : 'password'"
                  dense
                  hide-details
                  readonly
                />
                <v-btn icon small class="ml-1" @click="showPassword = !showPassword">
                  <v-icon small>{{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                </v-btn>
              </div>

              <div class="d-flex align-center" style="gap: 8px;">
                <v-switch
                  :input-value="passwordSettings.password_on"
                  color="success"
                  hide-details
                  readonly
                  class="mt-0 pt-0"
                />
                <span class="body-2">Password ON</span>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="mt-2" outlined>
          <v-data-table
            :headers="ipHeaders"
            :items="ipList"
            :footer-props="{ 'items-per-page-options': [10, 50, 100, 200] }"
          >
            <template #top>
              <div class="d-flex align-center px-4 py-2" style="height: 48px">
                <span class="body-1 black--text">White IP list</span>
                <v-btn
                  v-if="canEdit"
                  outlined
                  small
                  color="success"
                  class="ml-4"
                  @click="isIpDialogOpen = true"
                >
                  Add IP Address
                </v-btn>
              </div>
              <v-divider />
            </template>

            <template #item.index="{ index }">
              {{ index + 1 }}
            </template>

            <template #item.comment="{ item }">
              {{ item.comment || '—' }}
            </template>

            <template #item.action="{ item }">
              <v-btn v-if="canEdit" icon color="grey" :loading="removingIp === item.ip" @click="removeIp(item)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>

        <v-card class="mt-2" outlined>
          <v-expansion-panels v-model="nsPanel" flat>
            <v-expansion-panel>
              <v-expansion-panel-header v-slot="{ open }" class="main-settings-tab__expansion-header px-4">
                <div class="d-flex align-center">
                  <span class="body-1 black--text">Main Domain Name Servers</span>
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-icon small class="ml-1" v-on="on">mdi-information</v-icon>
                    </template>
                    Name servers assigned to the main domain
                  </v-tooltip>
                  <v-chip
                    v-if="!loading && mainDomainData"
                    small
                    outlined
                    :color="mainDomainData && mainDomainData.status === 'active' ? 'success' : 'grey'"
                    class="ml-3 text-capitalize"
                  >
                    {{ mainDomainData.status}}
                  </v-chip>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content class="main-settings-tab__expansion-content">
                <v-divider />
                  <div class="px-4">
                      <template v-if="nsList.length">
                          <div
                              v-for="(ns, index) in nsList"
                              :key="index"
                          >
                              <div class="d-flex align-center py-3">
                                  <span class="body-1 font-weight-medium">NS{{ ns.settings?.priority }}:&nbsp;</span>
                                  <span class="">{{ ns.name.toUpperCase() }}</span>
                                  <v-btn
                                      icon
                                      x-small
                                      class="ml-2"
                                      @click="copyToClipboard(ns.name)"
                                  >
                                      <v-icon small color="success">mdi-content-copy</v-icon>
                                  </v-btn>
                              </div>
                              <v-divider v-if="index < nsList.length - 1" />
                          </div>
                      </template>
                      <div
                          v-else
                      >
                          <div class="main-settings-tab__empty-ns">
                              <div class="pt-4 grey--text text-uppercase subtitle-2">
                                  No name servers configured
                              </div>
                          </div>
                      </div>
                  </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>

        <drawer-dialog v-model="isIpDialogOpen" width="70%">
          <main-settings-ip-form
            v-if="isIpDialogOpen"
            ref="ipForm"
            :is-loading="ipLoading"
            @close="isIpDialogOpen = false"
            @save="addIp"
          />
        </drawer-dialog>
      </div>
    </div>
  </div>

</template>

<script>
  import { API } from '@/utils/api'
  import { DrawerDialog } from '@/components/core/DrawerDialog'
  import mirrorMixin from '@/modules/mirrors-redirectors/mixins/mirror'
  import MainSettingsIpForm from './MainSettingsIpForm.vue'
  import GeoRulesEditor from '../MirrorsGroups/GeoRulesEditor.vue'

  export default {
    name: 'MainSettingsTab',
    mixins: [mirrorMixin],
    components: { DrawerDialog, MainSettingsIpForm, GeoRulesEditor },
    props: {
      project: {
        type: Number,
        default: null,
      },
      canEdit: {
        type: Boolean,
        default: false,
      },
      canReadFeatureConfig: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        loading: false,
        projectConfig: null,
        mainDomainData: null,
        featureConfig: null,
        rules: [],
        panel: null,
        settingsLoading: false,
        showPassword: false,
        passwordSettings: {
          login: '',
          password: '',
          password_on: false,
        },
        isIpDialogOpen: false,
        ipLoading: false,
        removingIp: null,
        nsPanel: 0,
        ipHeaders: [
          { text: '#', value: 'index', sortable: false, width: '50px' },
          { text: 'IP Address', value: 'ip', sortable: true },
          { text: 'Description', value: 'comment', sortable: false },
          { text: 'Action', value: 'action', sortable: false, align: 'end' },
        ],
      }
    },
    computed: {
      hasProject() {
        return this.project != null
      },
      blockedGeoDisplay() {
        return this.formatBlockedGeo(this.rules).join(', ')
      },
      ipList() {
        return this.projectConfig?.settings?.ip_list?.items || []
      },
      nsList() {
        return (this.mainDomainData?.settings?.ns?.items || [])
          .filter(ns => ns?.enabled)
          .sort((a, b) => (a?.settings?.priority ?? Infinity) - (b?.settings?.priority ?? Infinity));
      },
    },
    watch: {
      project: {
        immediate: true,
        handler(entityId) {
          if (entityId) {
            this.loadMainSettings(entityId)
            return
          }
          this.projectConfig = null
          this.mainDomainData = null
          this.featureConfig = null
        },
      },
      featureConfig: {
        immediate: true,
        handler(val) {
          const blockedGeo = val?.settings?.geo_settings?.blocked_geo || []
          this.rules = this.parseBlockedGeo(blockedGeo)
        },
      },
      projectConfig: {
        immediate: true,
        handler(val) {
          if (!val) return
          const s = val.settings || {}
          this.passwordSettings = {
            login: s.login || '',
            password: s.password || '',
            password_on: s.password_on ?? false,
          }
        },
      },
    },
    methods: {
      async loadMainSettings(entityId) {
        this.loading = true
        this.projectConfig = null
        this.mainDomainData = null
        this.featureConfig = null
        try {
          const response = await API.call('/$v/mirrors-redirectors/main-settings/list.json', {
            entity_id: entityId,
          })

          this.projectConfig = response.data.project_config ?? null
          this.mainDomainData = response.data.main_domain ?? null
          this.featureConfig = response.data.feature_config ?? null
        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to load main domain')
        } finally {
          this.loading = false
        }
      },

      copyToClipboard(text) {
        navigator.clipboard.writeText(text).then(() => {
          this.$showSuccess('Copied to clipboard')
        })
      },
      async updateProjectSettings(settings) {
        return API.call('/$v/mirrors-redirectors/main-settings/update.json', {
          entity_id: this.project,
          project_config: {
            ...this.projectConfig,
            settings: {
              ...this.projectConfig?.settings,
              ...settings,
            },
          },
        })
      },
      parseBlockedGeo(blockedGeo) {
        const map = {}
        for (const entry of blockedGeo) {
          const dashIndex = entry.indexOf('-')
          const geo = dashIndex !== -1 ? entry.slice(0, dashIndex) : entry
          const device = dashIndex !== -1 ? entry.slice(dashIndex + 1) : 'all'
          if (!map[geo]) map[geo] = { geo, devices: [] }
          if (device === 'all') {
            map[geo].devices = ['all']
          } else if (!map[geo].devices.includes('all')) {
            map[geo].devices.push(device)
          }
        }
        return Object.values(map)
      },
      formatBlockedGeo(rules) {
        const result = new Set()
        for (const rule of rules) {
          if (!rule.geo) continue
          if (rule.devices.includes('all')) {
            result.add(rule.geo)
          } else {
            for (const device of rule.devices) {
              result.add(`${rule.geo}-${device}`)
            }
          }
        }
        return [...result]
      },
      async addIp(ips) {
        if (!this.canEdit) return

        const existingIps = new Set(this.ipList.map(i => i.ip))
        const uniqueNewIps = [...new Map(ips.map(i => [i.ip, i])).values()]
          .filter(i => !existingIps.has(i.ip))

        if (!uniqueNewIps.length) {
          this.$showError('All IP already exist')
          return
        }

        if (uniqueNewIps.length !== ips.length) {
          this.$showError('IP duplication and omitting')
        }

        this.ipLoading = true
        try {
          await this.updateProjectSettings({
            ip_list: {
              items: [...this.ipList, ...uniqueNewIps],
            },
          })
          this.$showSuccess('Ip list updated successfully')
          await this.loadMainSettings(this.project)
          this.isIpDialogOpen = false
          this.$refs.ipForm?.reset()
        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to add IP address')
        } finally {
          this.ipLoading = false
        }
      },
      async removeIp(item) {
        if (!this.canEdit) return

        const updatedIpList = this.ipList.filter(i => i.ip !== item.ip)
        this.removingIp = item.ip
        this.ipLoading = true
        try {
          await this.updateProjectSettings({
            ip_list: {
              items: updatedIpList,
            },
          })
          this.$showSuccess('IP address removed successfully')
          await this.loadMainSettings(this.project)
        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to remove IP address')
        } finally {
          this.removingIp = null
          this.ipLoading = false
        }
      },

      addRule() {
        this.panel = 0
        this.rules.push({ geo: null, devices: [] })
      },
      removeRule(index) {
        this.rules.splice(index, 1)
      },
      updateRuleField(index, field, value) {
        if (!this.rules[index]) return
        this.$set(this.rules[index], field, value)
      },

      cancelRules() {
        const blockedGeo = this.featureConfig?.settings?.geo_settings?.blocked_geo || []
        this.rules = this.parseBlockedGeo(blockedGeo)
      },
      async saveRules() {
        if (!this.canEdit) return

        this.settingsLoading = true
        try {
          const updatedFeatureConfig = {
            ...this.featureConfig,
            settings: {
              ...this.featureConfig?.settings,
              geo_settings: {
                ...this.featureConfig?.settings?.geo_settings,
                blocked_geo: this.formatBlockedGeo(this.rules),
              }
            }
          }
          await API.call('/$v/mirrors-redirectors/settings/update.json', {
            entity_id  : this.project,
            feature_config  : updatedFeatureConfig,
          })
          this.$showSuccess('Geo rules updated successfully')
          await this.loadMainSettings(this.project)
        } finally {
          this.settingsLoading = false
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
  .main-settings-tab {
    padding-right: 0px;

    &__empty-ns {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100px;
    }

    &__geo-header {
      min-height: 56px;
      display: flex;
      align-items: center;
    }

    &__expansion-header {
      min-height: 56px !important;
    }
    &__expansion-content {
      ::v-deep .v-expansion-panel-content__wrap {
        padding: 0;
      }
    }
  }
</style>
